import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useThemeContext } from '@nxsys/context/AppContextProvider/ThemeContextProvider';
import { Dropdown } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { getColumns } from './column';
import { useGetInvoiceList } from '@nxsys/hooks/api';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import { ActionsHeader } from '../components/ActionsHeader';
import { GetInvoiceListParams } from '@nxsys/types/api';
import {
  useCreditInvoice,
  useDownloadAllInvoice,
  useDownloadInvoice,
  useMarkAllAsPaidInvoice,
  useMarkAsPaidInvoice,
  usePreviewInvoice,
  useSendInvoice,
  useSendMailRaiseInvoice,
} from '@nxsys/hooks/api/invoice/useInvoiceApi';
import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { showMessage } from '@nxsys/utils/message';
import { Invoice } from '@nxsys/types/models/invoice';
import { includes, toNumber } from 'lodash';
import moment from 'moment';
import { formatMoney } from '@nxsys/utils/number-helper';
import { formatStatus, showCurrencyByCountry } from '@nxsys/utils';
import { csvConfigExport } from '@nxsys/utils/export';
import { ExportToCsv } from 'export-to-csv';
import CreditInvoiceModal from '../components/CreditInvoiceModal';
import MarkAsPaidModal from '../components/MarkAsPaidModal';
import RaiseERCostModal from '../components/RaiseERCostModal';
import { InvoiceTypeEnum } from '@nxsys/constants/AppEnums';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';
import { IconArrowExport } from '@nxsys/components/AppIconSvg';
import { Icons } from '@nxsys/types/icons';

const DEFAULT_PAGE_SIZE = 20;

const InvoiceListing = () => {
  const { messages } = useTypedIntl();
  const { theme } = useThemeContext();
  const navigate = useNavigateWithQuery();

  const { user, isBureau, isBureauEmployer, isAgency, payrollCountry } =
    useSelector((state: AppState) => state.appStore);

  const { value, loading, run, refetch } = useGetInvoiceList();
  const invoiceList = value?.data ?? [];
  const lastArgs = value?.lastArgs ?? {};

  const { run: previewInvoiceRun, loading: isPreviewInvoicePending } =
    usePreviewInvoice();

  const { run: sendInvoiceRun, loading: isSendInvoicePending } =
    useSendInvoice();

  const {
    run: sendMailRaiseInvoiceRun,
    loading: isSendMailRaiseInvoicePending,
  } = useSendMailRaiseInvoice();

  const { run: downloadInvoiceRun, loading: isDownloadInvoicePending } =
    useDownloadInvoice();

  const { run: downloadAllInvoiceRun, loading: isDownloadAllInvoicePending } =
    useDownloadAllInvoice();

  const { run: creditInvoiceRun, loading: isCreditInvoicePending } =
    useCreditInvoice();

  const { run: markAsPaidInvoiceRun, loading: isMarkAsPaidInvoicePending } =
    useMarkAsPaidInvoice();

  const {
    run: markAllAsPaidInvoiceRun,
    loading: isMarkAllAsPaidInvoicePending,
  } = useMarkAllAsPaidInvoice();

  const [selectedRows, setSelectedRows] = useState<Invoice[]>([]);

  const [openCreditInvoice, setOpenCreditInvoice] = useState(false);
  const toggleCreditInvoice = () => setOpenCreditInvoice(!openCreditInvoice);

  const [openMarkAsPaid, setOpenMarkAsPaid] = useState<{
    open: boolean;
    data: Invoice[];
  }>({
    open: false,
    data: [],
  });
  const toggleMarkAsPaid = () =>
    setOpenMarkAsPaid({
      open: !openMarkAsPaid.open,
      data: openMarkAsPaid.data,
    });

  const [openRaiseERCost, setOpenRaiseERCost] = useState(false);
  const toggleRaiseERCost = () => setOpenRaiseERCost(!openRaiseERCost);

  useEffect(() => {
    run({
      page: 0,
      size: DEFAULT_PAGE_SIZE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useNotificationSocket({
    eventName: `send-mail-invoice-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['common.sendMailSuccess'],
      });
      refetch();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['common.sendMailFailed'],
      });
      refetch();
    },
  });

  const handleChangePagination = (page: number, pageSize: number) => {
    run({
      ...lastArgs,
      page: page - 1,
      size: pageSize,
    });
  };

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['invoices.title']}
        separator=""
        title={''}
      />
    );
  }, [messages]);

  const getMenuItems = () => {
    return [
      {
        key: 'raiseERCost',
        label: messages['invoices.raiseERCost'],
        onClick: toggleRaiseERCost,
      },
      ...(!isBureau
        ? [
            {
              key: 'markAsPaid',
              label: messages['invoices.markAsPaid'],
              onClick: handleOpenMarkAsPaid,
            },
            {
              key: 'markAllAsPaid',
              label: messages['invoices.markAllAsPaid'],
              onClick: handleMarkAllAsPaid,
            },
          ]
        : []),
      {
        key: 'emailInvoice',
        label: messages['invoices.emailInvoice'],
        onClick: handleSendInvoiceSelected,
      },
      {
        key: 'creditInvoice',
        label: messages['invoices.creditInvoice'],
        onClick: handleOpenCreditInvoice,
      },
      {
        key: 'export',
        label: messages['invoices.export'],
        onClick: handleExportSelectedInvoice,
      },
      {
        key: 'exportAll',
        label: messages['invoices.exportAll'],
        onClick: handleExportAllInvoice,
      },
      {
        key: 'download',
        label: messages['invoices.download'],
        onClick: handleDownloadSelectedInvoice,
      },
      {
        key: 'downloadAll',
        label: messages['invoices.downloadAll'],
        onClick: handleDownloadAllInvoice,
      },
    ];
  };

  const renderRightContent = () => {
    if (isAgency || isBureauEmployer) {
      return (
        <div className="flex items-center gap-2">
          <AppButton
            variant="outlined"
            type="primary"
            leftIcon={<IconArrowExport />}
            name={messages['invoices.export']}
            onClick={handleExportSelectedInvoice}
          />
          <AppButton
            icon={<Icons.IconDownload color={'#fff'} />}
            type="primary"
            name={messages['invoices.download']}
            onClick={handleDownloadSelectedInvoice}
          />
        </div>
      );
    }

    const menuProps = {
      items: getMenuItems(),
    };

    return (
      <div className="flex items-center gap-2">
        <AppButton
          type="primary"
          name={messages['invoices.addNew']}
          onClick={() => navigate('/invoices/create-invoice')}
          leftIcon={<PlusOutlined />}
        />
        <Dropdown menu={menuProps}>
          <AppButton
            type="primary"
            name={messages['common.action']}
            rightIcon={<DownOutlined />}
          />
        </Dropdown>
      </div>
    );
  };

  const handleSearch = (value: string) => {
    run({
      ...lastArgs,
      page: 0,
      search: value,
    });
  };

  const handleFilter = (params: GetInvoiceListParams) => {
    run({
      page: 0,
      size: lastArgs.size,
      search: lastArgs.search,
      ...params,
    });
  };

  const [downloadLoadingId, setDownloadLoadingId] = useState<string | null>(null);

  const handlePreviewInvoice = async (id: string) => {
    if (!id) return;
    setDownloadLoadingId(id);
    try {
      const res = await previewInvoiceRun({ id });
      if (res?.data) {
        window.open(res.data, '_blank');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadLoadingId(null);
    }
  };

  const handleSendInvoice = async (ids: string[]) => {
    try {
      await sendInvoiceRun({
        ids,
        templateKey: 'invoice',
      });
      refetch();
    } catch (error) {
      console.error(error);
    }
  };

  const handleSendMailRaiseInvoice = async (id: string) => {
    try {
      await sendMailRaiseInvoiceRun({ id });
      refetch();
    } catch (error) {
      console.error(error);
    }
  };

  const handleSendInvoiceSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleSendInvoice(selectedRows.map((row) => row.id));
  };

  const handleExportInvoice = (data: Invoice[]) => {
    if (!data?.length) {
      return;
    }
    const dataExport = data.map((item: any) => ({
      invoiceNo: isBureau ? item?.bureauInvoiceNo: item?.invoiceNo || '',
      invoicedDate: item?.invoicedDate
        ? moment(item?.invoicedDate).tz('Europe/London').format('DD/MM/YYYY')
        : '',
      from: item?.companyRef?.companyName || '',
      to: item?.agencyRef?.name || '',
      net: formatMoney(
        toNumber(item?.totalNet) || 0,
        showCurrencyByCountry(payrollCountry),
      ),
      vat: formatMoney(
        toNumber(item?.totalVat) || 0,
        showCurrencyByCountry(payrollCountry),
      ),
      total: formatMoney(
        toNumber(item?.totalPrices) || 0,
        showCurrencyByCountry(payrollCountry),
      ),
      paid: formatMoney(
        toNumber(item?.receipt) || 0,
        showCurrencyByCountry(payrollCountry),
      ),
      outstanding: formatMoney(
        toNumber(item?.outstanding) || 0,
        showCurrencyByCountry(payrollCountry),
      ),
      status: formatStatus(item?.sentStatus) || 'Inactive',
    }));

    const headers = [
      'Inv No',
      'Date',
      'From',
      'To',
      'Net',
      'VAT',
      'Total',
      'Paid',
      'Outstanding',
      'Sent Status',
    ];

    const exportName = csvConfigExport(`Invoice-export`, headers);
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);
  };

  const handleExportSelectedInvoice = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExportInvoice(selectedRows);
  };

  const [isExportingAll, setIsExportingAll] = useState(false);

  const handleExportAllInvoice = async () => {
    if (invoiceList.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['invoices.empty'],
      });
      return;
    }
    try {
      setIsExportingAll(true);
      const MAX_EXPORT_SIZE = 10000;
      const res = await run({
        page: 0,
        size: Math.min(value?.totalDocs || 0, MAX_EXPORT_SIZE),
        ...(!isBureau ? { isNexus: true } : {}),
      });
      handleExportInvoice(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsExportingAll(false);
    }
  };

  const handleDownloadSelectedInvoice = async () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    try {
      const res = await downloadInvoiceRun({
        ids: selectedRows.map((row) => row.id),
      });
      if (res?.data) {
        window.open(res.data, '_blank');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownloadAllInvoice = async () => {
    try {
      const res = await downloadAllInvoiceRun();
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['invoices.downloadAllSuccess'],
        });
      } else {
        showMessage({
          type: 'error',
          content: messages['invoices.downloadAllFailed'],
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenCreditInvoice = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    setOpenCreditInvoice(true);
  };

  const handleCreditInvoice = async (date: string) => {
    if (selectedRows.length === 0) return;
    try {
      const res = await creditInvoiceRun({
        ids: selectedRows.map((row) => row.id),
        creditDate: date,
      });
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['invoices.creditInvoiceSuccess'],
        });
        toggleCreditInvoice();
        await refetch();
      } else {
        showMessage({
          type: 'error',
          content: messages['invoices.creditInvoiceFailed'],
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenMarkAsPaid = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    const selectedRowValues = invoiceList?.filter(
      (item) =>
        includes(
          selectedRows.map((row) => row.id),
          item?.id,
        ) &&
        item?.invoiceStatus !== 'CREDITED' &&
        toNumber(item.outstanding) > 0,
    );
    if (!selectedRowValues.length) {
      showMessage({
        type: 'error',
        content: messages['invoices.invoiceAlreadyCreditOrPaid'],
      });
      return;
    }
    setOpenMarkAsPaid({
      open: true,
      data: selectedRowValues,
    });
  };

  const handleMarkAsPaid = async (
    datePaid: string,
    receipt: number,
    reference: string,
  ) => {
    if (!openMarkAsPaid.data?.length) return;
    try {
      const res = await markAsPaidInvoiceRun({
        ids: openMarkAsPaid.data.map((item) => item.id),
        datePaid,
        receipt: toNumber(receipt),
        reference,
      });
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['invoices.markAsPaidSuccess'],
        });
        await refetch();
        toggleMarkAsPaid();
      } else {
        showMessage({
          type: 'error',
          content: messages['invoices.markAsPaidFailed'],
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleMarkAllAsPaid = () => {
    if (!value?.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['invoices.noOutstandingInvoices'],
      });
      return;
    }
    AppConfirmation.open({
      title: messages['invoices.markAllAsPaid'],
      paragraph: messages['invoices.markAllAsPaidConfirm'],
      type: 'warning',
      icon: 'IconWarning',
      okText: messages['common.confirm'],
      onConfirm: async () => {
        const res = await markAllAsPaidInvoiceRun({
          datePaid: new Date() as any,
          receipt: 0,
        });
        if (res.success) {
          showMessage({
            type: 'success',
            content: messages['invoices.markAllAsPaidSuccess'],
          });
          await refetch();
        } else {
          showMessage({
            type: 'error',
            content: messages['invoices.markAllAsPaidFailed'],
          });
        }
      },
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFilter({
        ...lastArgs,
        page: 0,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleFilter({
        ...lastArgs,
        page: 0,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleViewInvoiceDetail = (id: string) => {
    navigate(`/invoices/invoice/${id}`);
  };

  const isFiltering =
    !!lastArgs?.search ||
    !!lastArgs?.isOutstanding ||
    !!lastArgs?.isPaid ||
    !!lastArgs?.startDate ||
    !!lastArgs?.endDate ||
    !!lastArgs?.invoiceNo ||
    !!lastArgs?.totalPrices;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={
        isPreviewInvoicePending ||
        isSendInvoicePending ||
        isDownloadInvoicePending ||
        isDownloadAllInvoicePending ||
        isSendMailRaiseInvoicePending ||
        isMarkAllAsPaidInvoicePending ||
        isExportingAll
      }
    >
      <AppCard>
        <AppsHeader>
          <ActionsHeader onSearch={handleSearch} onFilter={handleFilter} />
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            loading={loading}
            data={invoiceList ?? []}
            page={lastArgs?.page ?? 0}
            pageSize={lastArgs?.size ?? DEFAULT_PAGE_SIZE}
            total={value?.totalDocs ?? 0}
            titleEmpty={messages['invoices.empty']}
            columns={getColumns({
              messages,
              theme,
              role: user?.role ?? '',
              handlePreviewInvoice,
              handleSendInvoice,
              invoiceType: InvoiceTypeEnum.INVOICE,
              isBureau,
              isBureauEmployer,
              handleSendMailRaiseInvoice,
              handleViewInvoiceDetail,
              payrollCountry,
              downloadLoadingId,
            })}
            onChangePagination={handleChangePagination}
            onChangeTable={onChangeTable}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            scroll={{ x: 2500 }}
            noDataType={isFiltering ? 'noDataFound' : (isAgency || isBureauEmployer) ? 'noDataToShow' : 'noDataCreated'}
            addNewBtnOnClick={(isAgency || isBureauEmployer) ? undefined : () => navigate('/invoices/create-invoice')}
            dataTypeName={messages['common.invoice']}
          />
        </AppsContent>
      </AppCard>
      {openCreditInvoice && (
        <CreditInvoiceModal
          isOpen={openCreditInvoice}
          toggle={toggleCreditInvoice}
          onSubmit={handleCreditInvoice}
          isCreditInvoicePending={isCreditInvoicePending}
        />
      )}
      {openMarkAsPaid.open && openMarkAsPaid.data && (
        <MarkAsPaidModal
          isOpen={openMarkAsPaid.open}
          toggle={toggleMarkAsPaid}
          onSubmit={handleMarkAsPaid}
          isMarkAsPaidPending={isMarkAsPaidInvoicePending}
          data={openMarkAsPaid.data}
        />
      )}
      <RaiseERCostModal
        isOpen={openRaiseERCost}
        toggle={toggleRaiseERCost}
        refetch={refetch}
      />
    </AppsContainer>
  );
};

export default InvoiceListing;
